import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import {
  Frequency,
  frequencies,
  monthNames,
  timeBands,
  weekdays,
} from '../../data/booking';
import type { OrderRecurrence } from '../../data/payment';
import { colors, hairline, radii, screenPadding, spacing } from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

export type Recurrence = {
  frequency: Frequency;
  /** Short weekday name, as in `weekdays`. */
  day: string;
  /** Key of the chosen band, as in `timeBands`. */
  band: string;
};

type Props = {
  visible: boolean;
  /** What is already set, so reopening does not start from scratch. */
  recurrence: Recurrence | null;
  onClose: () => void;
  onConfirm: (recurrence: Recurrence) => void;
};

/** "Every Thursday, Afternoon (12 PM – 5 PM)" - how it reads back in the cart. */
export const describeRecurrence = (value: Recurrence): string => {
  const band = timeBands.find(item => item.key === value.band);
  const cadence =
    value.frequency === 'weekly'
      ? `Every ${value.day}`
      : value.frequency === 'fortnightly'
      ? `Every other ${value.day}`
      : `Monthly on ${value.day}`;

  return band ? `${cadence}, ${band.label} (${band.hours})` : cadence;
};

/**
 * The next date, on or after `today`, that falls on `recurrence.day`.
 *
 * Correct regardless of `frequency`: fortnightly and monthly only change when
 * the visit AFTER this one lands, never the first - that always happens on
 * the next matching weekday, the same as a weekly one would.
 */
const nextOccurrence = (recurrence: Recurrence, today: Date): Date => {
  const targetIndex = weekdays.indexOf(recurrence.day);
  // `getDay` is Sunday-first; shift so Monday is 0, matching `weekdays`.
  const todayIndex = (today.getDay() + 6) % 7;
  const diff = (targetIndex - todayIndex + 7) % 7;
  const next = new Date(today);
  next.setDate(next.getDate() + diff);
  return next;
};

/**
 * "Next: Thu, 12 Jun" - how a recurring schedule's first visit reads once it
 * is a booking, as opposed to `describeRecurrence`'s "Every Thursday…", which
 * is the pattern rather than a date. A booking needs both: this is what goes
 * in `when`, the pattern is what goes in `cadence`.
 */
export const describeFirstVisit = (recurrence: Recurrence, today: Date): string => {
  const date = nextOccurrence(recurrence, today);
  return `Next: ${recurrence.day}, ${date.getDate()} ${monthNames[date.getMonth()].slice(0, 3)}`;
};

/** The app's word for a cadence, in the server's. Only one of the three differs. */
const SERVER_FREQUENCY: Record<Frequency, OrderRecurrence['frequency']> = {
  weekly: 'weekly',
  fortnightly: 'biweekly',
  monthly: 'monthly',
};

/**
 * The opening hour of each band, as `HH:mm`.
 *
 * The plan endpoint wants one time and the customer chose a window. Its start
 * is the only reading that cannot promise them a visit earlier than the window
 * they picked - see `OrderRecurrence`.
 */
const BAND_OPENS: Record<string, string> = {
  morning: '08:00',
  afternoon: '12:00',
  evening: '17:00',
};

/** `YYYY-MM-DD` in local time - `toISOString` would shift the day in IST. */
const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

/**
 * The picker's answer, in the terms `POST /bookings/recurring-plans` accepts.
 *
 * Lives here rather than in the cart because everything it has to know - that
 * `weekdays` is Monday-first while the server counts from Sunday, that a band
 * is a window and not an instant - is this file's own subject.
 */
export const toOrderRecurrence = (
  recurrence: Recurrence,
  today: Date,
): OrderRecurrence => {
  // `weekdays` is Monday-first; the server counts Sunday as 0.
  const mondayFirst = weekdays.indexOf(recurrence.day);
  const sundayFirst = (mondayFirst + 1) % 7;

  return {
    frequency: SERVER_FREQUENCY[recurrence.frequency],
    /*
     * Sent for every cadence, monthly included. The server ignores it unless
     * the frequency is weekly or biweekly, and withholding it would mean this
     * function had to know that rule too.
     */
    daysOfWeek: [sundayFirst],
    timeOfDay: BAND_OPENS[recurrence.band] ?? '09:00',
    startDate: isoDate(nextOccurrence(recurrence, today)),
  };
};

/**
 * Setting up a repeating booking.
 *
 * Three choices on one surface: how often, which day, roughly when. A band
 * rather than an exact time on purpose - a standing weekly slot cannot promise
 * 4:00 PM every week without holding an hour hostage in the schedule, and
 * "Afternoon" is a promise that can actually be kept.
 *
 * Nothing is committed until Confirm; the sheet holds its own draft so backing
 * out leaves the cart's existing schedule alone.
 */
function RecurringSheet({ visible, recurrence, onClose, onConfirm }: Props) {
  const [frequency, setFrequency] = useState<Frequency>(
    recurrence?.frequency ?? 'weekly',
  );
  const [day, setDay] = useState<string>(recurrence?.day ?? weekdays[3]);
  const [band, setBand] = useState<string>(recurrence?.band ?? timeBands[1].key);

  const draft: Recurrence = { frequency, day, band };

  return (
    <BottomSheet visible={visible} onClose={onClose} heightRatio={0.86}>
      <View style={styles.header}>
        <Text variant="titleLg" style={styles.headerTitle}>
          Recurring Schedule
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          hitSlop={10}
        >
          <Icon
            name="plus"
            size={18}
            color={colors.inkMuted}
            strokeWidth={2.2}
            style={styles.closeGlyph}
          />
        </Pressable>
      </View>

      <ScrollView
        contentContainerStyle={styles.body}
        showsVerticalScrollIndicator={false}
      >
        {/* --------------------------------------------------- how often */}
        <Text variant="labelSm" style={styles.label}>
          Select Frequency
        </Text>

        <View style={styles.options}>
          {frequencies.map(option => {
            const active = option.key === frequency;

            return (
              <Pressable
                key={option.key}
                accessibilityRole="radio"
                accessibilityState={{ selected: active }}
                accessibilityLabel={`${option.label}. ${option.blurb}`}
                onPress={() => setFrequency(option.key)}
                style={[styles.option, active ? styles.optionOn : null]}
              >
                {/* A ring with a fill, not a tick: these are exclusive, and a
                    tick reads as a checkbox that could be one of several. */}
                <View style={[styles.radio, active ? styles.radioOn : null]}>
                  {active ? <View style={styles.radioDot} /> : null}
                </View>

                <View style={styles.optionText}>
                  <Text variant="subtitle">{option.label}</Text>
                  <Text variant="caption" color={colors.inkMuted}>
                    {option.blurb}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>

        {/* ------------------------------------------------------ which day */}
        <Text variant="labelSm" style={styles.label}>
          Preferred Day
        </Text>

        <View style={styles.chips}>
          {weekdays.map(name => {
            const active = name === day;
            return (
              <Pressable
                key={name}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                accessibilityLabel={name}
                onPress={() => setDay(name)}
                style={[styles.chip, active ? styles.chipOn : null]}
              >
                <Text
                  variant="caption"
                  align="center"
                  color={active ? colors.onPrimary : colors.ink}
                >
                  {name}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {/* ----------------------------------------------------- which band */}
        <Text variant="labelSm" style={styles.label}>
          Preferred Time Slot
        </Text>

        <View style={styles.bands}>
          {timeBands.map(option => {
            const active = option.key === band;
            return (
              <Pressable
                key={option.key}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                accessibilityLabel={`${option.label}, ${option.hours}`}
                onPress={() => setBand(option.key)}
                style={[styles.band, active ? styles.bandOn : null]}
              >
                <Text
                  variant="subtitle"
                  align="center"
                  color={active ? colors.onPrimary : colors.ink}
                >
                  {option.label}
                </Text>
                <Text
                  variant="caption"
                  align="center"
                  color={active ? colors.onPrimary : colors.inkMuted}
                >
                  {option.hours}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <Button label="Confirm Schedule" onPress={() => onConfirm(draft)} />
        <Text variant="caption" align="center" color={colors.inkMuted}>
          {describeRecurrence(draft)}
        </Text>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },
  headerTitle: {
    flex: 1,
  },
  // A rotated plus - the icon set has no cross glyph.
  closeGlyph: {
    transform: [{ rotate: '45deg' }],
  },

  body: {
    paddingBottom: spacing.lg,
  },
  label: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },

  options: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  optionOn: {
    backgroundColor: colors.surfaceAccent,
    borderColor: colors.primary,
  },
  optionText: {
    flex: 1,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.borderStrong,
  },
  radioOn: {
    borderColor: colors.primary,
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.primary,
  },

  chips: {
    flexDirection: 'row',
    gap: spacing.xs,
    paddingHorizontal: screenPadding,
  },
  chip: {
    flex: 1,
    paddingVertical: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    ...hairline,
  },
  chipOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },

  bands: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  band: {
    flex: 1,
    gap: 2,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xs,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  bandOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },

  footer: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
});

export default RecurringSheet;
